import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { signIn, signOut } from '../util/amplifyAuth';
import { manageActivation } from "../components/API";
import { completeSignInAndRedirect } from "../util/authNav";
import PasswordInput from "../components/PasswordInput";
import PasswordRequirements, { usePasswordValidation } from "../components/PasswordRequirements";
import Spinner from "../components/Spinner/Spinner";
import "../styles/PasswordInput.css";
import "../styles/ResetPassword.css";

function ResetPassword() {
    const [step, setStep] = useState('loading'); // loading | form | error
    const [token, setToken] = useState(null);
    const [maskedEmail, setMaskedEmail] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [formError, setFormError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { valid: passwordValid } = usePasswordValidation(password);

    // Inject no-referrer meta tag to prevent token leaking via Referer header
    useEffect(() => {
        const meta = document.createElement('meta');
        meta.name = 'referrer';
        meta.content = 'no-referrer';
        document.head.appendChild(meta);
        return () => { document.head.removeChild(meta); };
    }, []);

    // Extract token from URL on mount, then clear it from the address bar
    useEffect(() => {
        const tokenParam = searchParams.get('token');
        if (!tokenParam) {
            setErrorMessage('No reset token provided. Please use the link from your email.');
            setStep('error');
            return;
        }

        setToken(tokenParam);
        window.history.replaceState({}, '', '/reset-password');
        validateToken(tokenParam);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const validateToken = async (tokenValue) => {
        try {
            const result = await manageActivation('validate-token', { token: tokenValue });

            if (result.valid && result.type === 'password-reset') {
                setMaskedEmail(result.maskedEmail || '');
                setStep('form');
            } else if (result.valid) {
                // Valid token but wrong type (activation token, not password-reset)
                setErrorMessage('This link is not a password reset link.');
                setStep('error');
            } else {
                switch (result.reason) {
                    case 'not_found':
                        setErrorMessage('This reset link is invalid. Please request a new one from the login page.');
                        break;
                    case 'expired':
                        setErrorMessage('This reset link has expired. Please request a new one from the login page.');
                        break;
                    case 'used':
                        setErrorMessage('This reset link has already been used. If you still need to reset your password, request a new link from the login page.');
                        break;
                    default:
                        setErrorMessage('This reset link is invalid.');
                }
                setStep('error');
            }
        } catch {
            setErrorMessage('Unable to validate your reset link. Please try again later.');
            setStep('error');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormError('');

        if (!passwordValid) {
            setFormError('Please meet all password requirements.');
            return;
        }
        if (password !== confirmPassword) {
            setFormError('Passwords do not match.');
            return;
        }

        setSubmitting(true);

        try {
            const result = await manageActivation('reset-password', {
                token,
                newPassword: password
            });

            if (result.success) {
                // Auto-sign-in with the new password
                try {
                    try { await signOut(); } catch {}
                    await signIn({ username: result.email, password });
                    await completeSignInAndRedirect(navigate);
                    return;
                } catch {
                    navigate('/login');
                    return;
                }
            } else if (result.error === 'password_policy') {
                setFormError(result.message || 'Password does not meet requirements.');
            } else if (result.error === 'invalid_token') {
                switch (result.reason) {
                    case 'expired':
                        setErrorMessage('This reset link has expired. Please request a new one.');
                        setStep('error');
                        break;
                    case 'used':
                        setErrorMessage('This reset link has already been used.');
                        setStep('error');
                        break;
                    default:
                        setErrorMessage('This reset link is no longer valid.');
                        setStep('error');
                }
            } else {
                setFormError('Something went wrong. Please try again.');
            }
        } catch {
            setFormError('Unable to reset password. Please try again later.');
        } finally {
            setSubmitting(false);
        }
    };

    // Loading state
    if (step === 'loading') {
        return (
            <div className="reset-page">
                <div className="reset-container">
                    <div className="reset-loading">
                        <Spinner size={40} color="#111827" />
                        <p>Validating your reset link...</p>
                    </div>
                </div>
            </div>
        );
    }

    // Error state
    if (step === 'error') {
        return (
            <div className="reset-page">
                <div className="reset-container">
                    <h1>Reset Password</h1>
                    <div className="reset-error">{errorMessage}</div>
                    <p className="reset-back-link">
                        Back to{" "}
                        <a href="/login" onClick={(e) => { e.preventDefault(); navigate('/login'); }}>Log in</a>
                    </p>
                </div>
            </div>
        );
    }

    // Form state
    return (
        <div className="reset-page">
            <div className="reset-container">
                <h1>Reset Password</h1>
                <p className="reset-subtitle">
                    Create a new password
                    {maskedEmail && <> for <strong>{maskedEmail}</strong></>}
                </p>

                {formError && <div className="reset-error">{formError}</div>}

                <form className="reset-form" onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label htmlFor="reset-password">New Password</label>
                        <PasswordInput
                            id="reset-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                        />
                        <PasswordRequirements password={password} />
                    </div>

                    <div className="form-group">
                        <label htmlFor="reset-confirm">Confirm Password</label>
                        <PasswordInput
                            id="reset-confirm"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                        />
                    </div>

                    <button
                        className="reset-submit"
                        type="submit"
                        disabled={submitting}
                    >
                        {submitting ? <Spinner size={20} color="#fff" /> : 'Reset Password'}
                    </button>
                </form>

                <p className="reset-back-link">
                    Back to{" "}
                    <a href="/login" onClick={(e) => { e.preventDefault(); navigate('/login'); }}>Log in</a>
                </p>
            </div>
        </div>
    );
}

export default ResetPassword;
